import { useEffect, useState } from 'react'
import { gp } from './gamePlayStyles'

export interface StatItem {
  value: string | number
  label: string
}

export interface GamePlayHeaderProps {
  title:             string
  subtitle?:         string
  stats:             [StatItem, StatItem]
  progress:          number        // 0–100
  progressMax?:      number        // aria-valuemax (defaults to 100)
  progressNow?:      number        // aria-valuenow (defaults to progress)
  onBack:            () => void
  backLabel:         string
  /** When set, listens to this element's scroll instead of window.scroll */
  scrollElementRef?: React.RefObject<HTMLElement | null>
}

const SCROLL_TOP_THRESHOLD = 10
const SCROLL_HIDE_THRESHOLD = 5

export default function GamePlayHeader({
  title,
  subtitle,
  stats,
  progress,
  progressMax = 100,
  progressNow,
  onBack,
  backLabel,
  scrollElementRef,
}: GamePlayHeaderProps) {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    let lastY = 0
    let ticking = false

    const onScroll = () => {
      if (ticking) return
      ticking = true

      requestAnimationFrame(() => {
        const el = scrollElementRef?.current
        const currentY = el ? el.scrollTop : (window.scrollY || window.pageYOffset)
        const diff = currentY - lastY

        if (currentY <= SCROLL_TOP_THRESHOLD) {
          setHidden(false)
        } else if (diff > SCROLL_HIDE_THRESHOLD) {
          setHidden(true)
        }

        lastY = currentY
        ticking = false
      })
    }

    const target: EventTarget = scrollElementRef?.current ?? window
    target.addEventListener('scroll', onScroll, { passive: true })
    return () => target.removeEventListener('scroll', onScroll)
  }, [scrollElementRef])

  const wrapStyle: React.CSSProperties = {
    ...gp.headerWrap,
    transform: hidden ? 'translateY(-100%)' : 'translateY(0)',
  }

  return (
    <div style={wrapStyle}>
      <header style={gp.header}>
        <button onClick={onBack} style={gp.backBtn} aria-label={backLabel}>
          ←
        </button>

        <div style={gp.center}>
          <span style={gp.title}>{title}</span>
          {subtitle && <span style={gp.subtitle}>{subtitle}</span>}
        </div>

        <div style={gp.statsCard}>
          <div style={gp.stat}>
            <span style={gp.statValue}>{stats[0].value}</span>
            <span style={gp.statLabel}>{stats[0].label}</span>
          </div>
          <span style={gp.statDivider} />
          <div style={gp.stat}>
            <span style={gp.statValue}>{stats[1].value}</span>
            <span style={gp.statLabel}>{stats[1].label}</span>
          </div>
        </div>
      </header>

      <div
        style={gp.progressTrack}
        role="progressbar"
        aria-valuenow={progressNow ?? Math.round(progress)}
        aria-valuemax={progressMax}
        aria-valuemin={0}
      >
        <div style={{ ...gp.progressFill, width: `${progress}%` }} />
      </div>
    </div>
  )
}
